import React, { useCallback, useContext } from 'react'
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext'

export const useSportMatchFilters = () => {

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_handlers } = sportsSeasonContext ?? {}
  const { handleAmmend_sportCompares } = sportsSeason_handlers ?? {}

  const callback = (res) => {
    handleAmmend_sportCompares(res)
  }

  const sectionRecords = useCallback((matches, teams) => {
    const result = getMatches_sectionRecords(matches, teams)
    callback(result)
    return result
  }, [])
  const matchesRemaining = useCallback((matches, selectedTeams) => {
    const result = getMatches_remaining(matches, selectedTeams)
    callback(result)
    return result
  }, [])
  const sharedMatches = useCallback((matches, selectedTeams) => {
    const result = getMatches_shared(matches, selectedTeams)
    callback(result)
    return result
  }, [])
  const teamCompares = useCallback((matches, selectedTeams) => {
    const result = getMatches_compares(matches, selectedTeams)
    callback(result)
    return result
  }, [])
  const sames = useCallback((matches, tms, selectedTeams) => {
    const result = getMatches_same(matches, tms, selectedTeams)
    callback(result)
    return result
  }, [])
  const opponents = useCallback((teamMatches, teamKey) => {
    const result = getMatches_opponents(teamMatches, teamKey)
    callback(result)
    return result
  }, [])

  return {
    sectionRecords,
    matchesRemaining,
    sharedMatches,
    teamCompares,
    sames,
    opponents

  }
}

const getMatches_sectionRecords = (matches, teams) => {

  if (!matches || !teams) return [];

  const _matches = _.cloneDeep(matches)
  _.forEach(_matches, (match) => {
    match.away.ls = match.away.levels + '_' + match.away.sections
    match.home.ls = match.home.levels + '_' + match.home.sections
  })

  const levelRecords = {}

  const allLevels = _.flatMap(teams, 'levels');
  const _allLevels = _.uniq(allLevels);

  _.forEach(_allLevels, (levelKey) => {

    levelRecords[levelKey] = {}

    // get all the matches where the home.level or away.level === levelKey
    const filteredMatches_level = _.filter(_matches, match => (match?.away?.levels === levelKey || match?.home?.levels === levelKey) && match._results);

    const allSections = _.flatMap(filteredMatches_level, 'sections');
    const _allSections = _.uniq(allSections);

    _.forEach(_allSections, (sectionKey) => {

      // get all the matches where the home.sections or away.sections === sectionKey
      const filteredMatches_section = _.filter(filteredMatches_level, match =>
        (match?.away?.sections === sectionKey || match?.home?.sections === sectionKey)
        && match._results
      )

      const record = {};

      record.totalMatches = _.pickBy(filteredMatches_section, m => (m?.away.ls !== m?.home.ls));
      record.wins = _.pickBy(record.totalMatches, m => (m?._results?.winner?.sections === sectionKey));

      const sectionRecord = {
        tm: _.size(record.totalMatches),
        w: _.size(record.wins)
      };

      sectionRecord.l = sectionRecord.tm - sectionRecord.w
      sectionRecord.wp = sectionRecord.tm > 0 ? (sectionRecord.w / (sectionRecord.tm)) : .500
      sectionRecord.wp = sectionRecord.wp.toFixed(3)
      sectionRecord.sectionKey = sectionKey

      levelRecords[levelKey][sectionKey] = sectionRecord
    })
  })

  return { levelRecords }

}

const getMatches_remaining = (matches, selectedTeams) => {
  if (!matches || selectedTeams.length !== 2) return [];
  if (selectedTeams && selectedTeams.length > 0) {
    const currentDate = new Date();
    const teamMatches = {}
    selectedTeams.forEach(team => {
      const _matches = _.filter(matches, function (m) {
        const matchStartDate = new Date(
          parseInt(m.startDate.split('/')[2]), // year
          parseInt(m.startDate.split('/')[0]) - 1, // month (zero-based)
          parseInt(m.startDate.split('/')[1]) // day
        );
        return m.teams && m.teams.includes(team) && matchStartDate > currentDate;
      }
      )
      teamMatches[team] = _matches
    })
    return { teamMatches }
  }
}

const getMatches_shared = (matches, selectedTeams) => {
  if (!matches || selectedTeams.length !== 2) return [];
  const sharedMatches = _.filter(matches, m =>
    m.teams && _.every(selectedTeams, team => _.includes(m.teams, team))
  );
  return { sharedMatches }
};

const getMatches_compares = (matches, selectedTeams) => {
  const _teamMatches = {}
  if (!matches || !selectedTeams) return
  selectedTeams.forEach(team => {
    const _matches = _.filter(matches, function (m) { return m.teams && m.teams.includes(team) && m._results; })
    _teamMatches[team] = _matches
  })
  if (Object.keys(selectedTeams).length > 1) {
    return getMatches_same(matches, _teamMatches, selectedTeams)
  }
}

const getMatches_same = (matches, tms, selectedTeams) => {

  const team1Key = Object.keys(tms)[0]
  const team2Key = Object.keys(tms)[1]
  const team3Key = Object.keys(tms)[2]

  const team1Matches = tms[team1Key]
  const team2Matches = tms[team2Key]
  const team3Matches = team3Key ? tms[team3Key] : null

  let team1Opponents = getMatches_opponents(team1Matches, team1Key)
  let team2Opponents = getMatches_opponents(team2Matches, team2Key)
  let team3Opponents = team3Key ? getMatches_opponents(team3Matches, team3Key) : null

  let _commonOpponents;

  if (team3Key) {
    _commonOpponents = team1Opponents && team2Opponents && team1Opponents.filter(item => team2Opponents.includes(item) && team3Opponents.includes(item));
  } else {
    _commonOpponents = team1Opponents && team2Opponents && team1Opponents.filter(item => team2Opponents.includes(item));
  }

  _commonOpponents = [...new Set(_commonOpponents)]

  const commonMatches = {}
  const directMatches = {}
  const opponentMatches = {}
  const teamResults = {}

  if (_commonOpponents) {
    _commonOpponents.forEach(co => {

      const _matches_common = _.filter(matches, m =>
        (m.teams && m._results && _.intersection(m.teams, [co, team1Key, team2Key]).length >= 2)
      );

      const _matches_direct = _.filter(matches, m =>
        (m.teams && m._results && _.intersection(m.teams, [team1Key, team2Key]).length === 2)
      );

      _.forEach(_matches_common, m => {
        commonMatches[m._itemKey] = commonMatches[m._itemKey] || m;
      });

      _.forEach(_matches_direct, m => {
        directMatches[m._itemKey] = directMatches[m._itemKey] || m;
      });
    });

    _commonOpponents.forEach(co => {
      const _matches_opp = _.filter(commonMatches, m => (m.teams && m.teams.includes(co)));
      opponentMatches[co] = _matches_opp;
    });

    _.forEach(selectedTeams, st => {
      const _matches_team = _.filter(commonMatches, m => (m.teams && m.teams.includes(st)));
      teamResults[st] = {}
      teamResults[st].matchCount = _matches_team.length;
      teamResults[st].wins = _.pickBy(_matches_team, m => (m?._results?.winner?.id === st));
      teamResults[st].w = _.size(teamResults[st].wins)
      teamResults[st].wp = (teamResults[st].w / (teamResults[st].matchCount))
    })

    return { commonMatches, directMatches, opponentMatches, teamResults }

  }

}

const getMatches_opponents = (teamMatches, teamKey) => {

  let team1Opponents = []

  teamMatches.forEach(tm => {
    const { teams } = tm
    teams.forEach(team => {
      if (team !== teamKey) {
        team1Opponents.push(team)
      }
    })
  })

  return team1Opponents
}